import type {
  DefaultError,
  QueryClient,
  QueryExecuteOptions,
  QueryKey,
} from '@tanstack/react-query';

import type { AsyncFilterDef, FilterOption } from './types';

/** Options for an `async` filter, loaded like any other query. */
export interface QueryFilterOptionsConfig<
  TPage,
  TPageKey extends QueryKey,
  TIds,
  TIdsKey extends QueryKey,
> {
  /** The query for the picker's search text, usually the list's query options: `(q) => customerListQueryOptions(storeId, { q })`. */
  search: (query: string) => QueryExecuteOptions<TPage, DefaultError, TPage, TPage, TPageKey>;
  toOptions: (data: TPage) => readonly FilterOption[];
  /**
   * Labels for values from the URL that the picker has not loaded yet
   * (`?customer=cus_1`): a query for those ids. Without it the chip shows the
   * raw value until the picker loads a page containing it.
   */
  resolve?: {
    query: (
      values: readonly string[],
    ) => QueryExecuteOptions<TIds, DefaultError, TIds, TIds, TIdsKey>;
    toOptions: (data: TIds) => readonly FilterOption[];
  };
  /** How long a loaded page of options stays fresh. Default one minute. */
  staleTime?: number;
}

const STALE_MS = 60_000;

/**
 * `loadOptions` (and `resolveOptions`) for an `async` filter, fetched through
 * `queryClient.query`: the options share the query cache, keys and
 * error handling of the screens they come from, instead of a private cache.
 *
 * ```ts
 * const customer: FilterDef<Order> = {
 *   key: 'customer', label: t('filters.customer'), type: 'async',
 *   ...queryFilterOptions(queryClient, {
 *     search: (q) => customerListQueryOptions(storeId, { q, limit: 20 }),
 *     toOptions: (page) => page.items.map((c) => ({ value: c.id, label: c.name, description: c.email })),
 *   }),
 * };
 * ```
 */
export function queryFilterOptions<
  TPage,
  TPageKey extends QueryKey,
  TIds = never,
  TIdsKey extends QueryKey = QueryKey,
>(
  queryClient: QueryClient,
  config: QueryFilterOptionsConfig<TPage, TPageKey, TIds, TIdsKey>,
): Pick<AsyncFilterDef, 'loadOptions' | 'resolveOptions'> {
  const staleTime = config.staleTime ?? STALE_MS;
  // `query` takes no outside signal; the table ignores an answer that
  // arrives after it moved on, and the page stays cached for the next open.
  const loadOptions = async (query: string) =>
    config.toOptions(await queryClient.query({ ...config.search(query), staleTime }));
  const { resolve } = config;
  if (!resolve) return { loadOptions };
  return {
    loadOptions,
    resolveOptions: async (values) =>
      resolve.toOptions(await queryClient.query({ ...resolve.query(values), staleTime })),
  };
}
